import * as LiveCellValues from 'notebook/structured-tables/LiveCellValues';
import {BaseTypedValueType, TableColumnType, TypedValue} from 'notebook/tables/TableConstants';
import {areTypedValuesEquivalent} from 'notebook/tables/typed-values/DataTypes';
import * as logging from 'util/Logging';

import {
  StructuredTableColumn,
  StructuredTableColumnGroups,
  StructuredTableKind,
} from '../StructuredTableConstants';

/**
 * Returns true if the cell containing the given evaluatedValue should be considered "stale" or
 * "out of date".  This happens when the "live value" of the cell is different than the given
 * evaluatedValue in cases when we are not in a row that has unsubmitted changes.
 *
 * Some edge cases for when we don't consider a value as out of date are:
 * - when it's a computing field and it's currently in a PENDING state
 * - when there is no 'live value' which would happen if it's a new row or a new column
 *
 * @param isCellInUnsubmittedRow - true if the row is unsubmitted, i.e. not finalized
 * @param column - the structured table column of the given cell
 * @param evaluatedValue - the typedValue of the given cell
 * @param liveCellValue - a "live" representation of what the cell is referring to for the given row, i.e.
 *                        the schema field of the sample that the row is referencing
 */
export default function cellHasOutdatedValue({
  isCellInUnsubmittedRow,
  structuredTableColumn,
  evaluatedValue,
  liveCellValue,
  tableKind,
  lockRowsAfterSubmit,
}: {
  isCellInUnsubmittedRow: boolean;
  structuredTableColumn: StructuredTableColumn;
  evaluatedValue: TypedValue | null;
  liveCellValue: LiveCellValues.LiveCellValue;
  tableKind?: StructuredTableKind;
  lockRowsAfterSubmit?: boolean;
}): boolean {
  // If rows are fully locked after submit, all submitted rows are not outdated. Currently relevant only
  // for inventory tables
  if (!isCellInUnsubmittedRow && lockRowsAfterSubmit) {
    return false;
  }

  if (
    evaluatedValue?.type !== BaseTypedValueType.COMPUTING &&
    // If a cell's row has unsubmitted changes, it is not considered outdated
    isCellInUnsubmittedRow &&
    // unless it's in a lookup column
    structuredTableColumn.columnType !== TableColumnType.DEDICATED_LOOKUP &&
    // or in an inventory table if it is not in the schema field value column or concentration column
    // TODO(BNCH-41303): hack for inventory tables where customEval fns return stale values, do not follow
    (tableKind !== StructuredTableKind.INVENTORY ||
      structuredTableColumn.columnType === TableColumnType.TAG_VALUE ||
      structuredTableColumn.columnType === TableColumnType.STUDIES ||
      structuredTableColumn.columnGroup === StructuredTableColumnGroups.CONCENTRATION)
  ) {
    return false;
  }

  // Don't show a row where we are still loading the computing state as outdated
  if (
    evaluatedValue?.type === BaseTypedValueType.COMPUTING &&
    evaluatedValue.computedState.status === 'pending'
  ) {
    return false;
  }

  // If live field value doesn't exist, or hasn't been loaded, assume the cell is up to date.
  else if (liveCellValue === 'NO_VALUE') {
    return false;
  } else {
    try {
      return !areTypedValuesEquivalent(evaluatedValue, liveCellValue);
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      logging.error('Failed to compare whether cell is outdated.', {extra: {evaluatedValue, liveCellValue}});
      return true;
    }
  }
}
